import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-temptation",
  title: "Bible verses about temptation: 10 passages, explained",
  h1: "Bible verses about temptation",
  description:
    "Ten passages on temptation and the way out, with notes on what each says — including why 1 Corinthians 10:13 promises an escape rather than an absence.",
  targets: [
    "bible verses about temptation",
    "scripture for resisting temptation",
    "what does the bible say about temptation",
  ],
  published: "2026-08-26",
  standfirst:
    "The most quoted verse on temptation promises a way out, not an easier time — and the way out is usually a door somebody has to walk through.",
  takeaways: [
    "1 Corinthians 10:13 promises a way of escape, not the removal of the temptation, and the escape has to be taken rather than waited for.",
    "James 1:14 locates the mechanism internally — drawn away by his own desire and enticed — which rules out blaming the object.",
    "Hebrews 4:15 says Jesus was tempted in every way, which makes temptation itself something other than evidence of failure.",
    "Matthew 26:41 pairs watching with praying, and admits the flesh is weak in the same sentence — the instruction assumes the weakness rather than denying it.",
  ],
  intro: [
    {
      type: "p",
      text: "Most people arrive at this subject at a specific moment, and it is usually not the moment of temptation — it is afterwards. That matters, because the passages below do two quite different jobs. Some of them describe the mechanism, which is useful before. Others are about what is true after, which is the half that people looking for a verse at eleven at night are generally not looking for and generally need more.",
    },
    {
      type: "p",
      text: "One clarification up front, since it changes how the whole list reads. The best-known verse in it does not promise that temptation will be lighter. It promises there will be a door. Whether the door gets used is a separate question, and the passages that follow are largely about making it easier to reach.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "No temptation has taken you except what is common to man. God is faithful, who will not allow you to be tempted above what you are able, but will with the temptation also make the way of escape, that you may be able to endure it.",
        ref: "1 Corinthians 10:13",
      },
      note: "Three claims, and the first is often skipped: what is common to man. Whatever it is, it is not unusual and you are not uniquely broken. The third is the practical one — a way of escape exists, which implies a physical direction to move in, and it is offered so that you can endure rather than so that you can feel nothing.",
    },
    {
      verse: {
        text: "But each one is tempted when he is drawn away by his own lust and enticed. Then the lust, when it has conceived, bears sin.",
        ref: "James 1:14–15",
      },
      note: "A sequence, described almost clinically: drawn, enticed, conceived, born. The value of it is that each stage is earlier than the one people usually try to intervene at. By the time something is being born there is no decision left to make; the decision was several stages back, and it looked like nothing at the time.",
    },
    {
      verse: {
        text: "For we don't have a high priest who can't be touched with the feeling of our infirmities, but one who has been in all points tempted like we are, yet without sin.",
        ref: "Hebrews 4:15",
      },
      note: "The most consoling sentence in the collection, and the one that settles a common confusion: being tempted is not the same as failing. If it were, this verse would be impossible. Anyone treating the arrival of a desire as proof of a corrupt heart is measuring by a standard scripture itself does not use.",
    },
    {
      verse: {
        text: "Watch and pray, that you don't enter into temptation. The spirit indeed is willing, but the flesh is weak.",
        ref: "Matthew 26:41",
      },
      note: "Said to friends who had fallen asleep, which is the tone to read it in — realistic rather than severe. Note the object: pray that you do not enter into it. The prayer is aimed at the approach, not at the struggle, which is a much earlier point of intervention than most people attempt.",
    },
    {
      verse: {
        text: "Be subject therefore to God. Resist the devil, and he will flee from you.",
        ref: "James 4:7",
      },
      note: "Two verbs, and the order is not decorative. Submission comes first; resistance on its own is willpower with a religious vocabulary. The promise attached is directional — he flees — which fits the pattern of the whole subject: something leaves, but only after something is done.",
    },
    {
      verse: {
        text: "But put on the Lord Jesus Christ, and make no provision for the flesh, for its lusts.",
        ref: "Romans 13:14",
      },
      note: "Provision is a logistics word — supplies laid in for a campaign. The instruction is about what is kept within reach rather than about what is felt in the moment, which makes it the most practical verse here for anything involving a device. An app removed on Tuesday is a provision not made.",
    },
    {
      verse: {
        text: "Can a man scoop fire into his lap, and his clothes not be burned?",
        ref: "Proverbs 6:27",
      },
      note: "A rhetorical question about proximity, aimed at people who believe they can manage the distance. The image concedes nothing to intention: the fire does not consult the plan. Read alongside Romans 13:14, the two make a single argument about arrangement rather than resolve.",
    },
    {
      verse: {
        text: "I made a covenant with my eyes; how then should I look lustfully at a young woman?",
        ref: "Job 31:1",
      },
      note: "A decision made once, in advance, about a category of situation — which is a fundamentally different act from deciding in the moment. Behavioural research on precommitment says the same thing in duller language. The word covenant is doing work: this is a binding agreement, not an intention.",
    },
    {
      verse: {
        text: "Set a watch, Yahweh, before my mouth. Keep the door of my lips.",
        ref: "Psalm 141:3",
      },
      note: "About speech rather than appetite, and included because the image is the useful one: a guard placed at a door, requested rather than mustered. The psalmist is asking for the intervention to happen at the threshold, which is exactly where anything automatic has to sit if it is going to help.",
    },
    {
      verse: {
        text: "For all that is in the world, the lust of the flesh, the lust of the eyes, and the pride of life, isn't the Father's, but is the world's.",
        ref: "1 John 2:16",
      },
      note: "An old threefold summary, and the middle term has aged strangely well. The lust of the eyes is not primarily about desire for a person; it is about wanting what you have seen, which is the entire operating model of an advertising-funded feed.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "the-way-out",
      text: "Taking the way out before you need it",
    },
    {
      type: "p",
      text: "Three of these passages point the same direction. Job makes the decision once, in advance. Romans says do not lay in the supplies. Proverbs asks whether proximity can be managed by intention, and answers no. Together they describe a strategy that is almost entirely about arrangement — what is within reach at eleven at night — and almost not at all about the strength of the resolution made at eleven at night, which is when everybody's is weakest.",
    },
    {
      type: "p",
      text: "That is the reasoning behind putting a real barrier on the two or three things you reach for without deciding, rather than a reminder you can dismiss. {How-a-prayer-lock-works} sets out the version of this we build, and why the thing that fills the gap matters as much as the gap itself.",
      links: { "How-a-prayer-lock-works": "/blog/christian-app-blocker-guide" },
    },
    {
      type: "h2",
      id: "a-prayer-for-the-moment",
      text: "A prayer for the moment before",
    },
    {
      type: "p",
      text: "Lord, you know this one; you were tempted in every way. Show me the way out you have promised, and give me the will to walk through it now rather than after. Set a watch at the door. And if I have already failed tonight, meet me there too. Amen.",
    },
    {
      type: "note",
      text: "If the pattern is compulsive rather than occasional, tell one person. Almost nothing in this list works alone, and scripture never assumes it is being read alone.",
    },
  ],
  faq: [
    {
      q: "Does God tempt anyone?",
      a: "James 1:13 says explicitly that God tempts no one, and the same passage locates the pull in our own desire. The word in Greek covers both testing and enticing, which is why the two ideas sometimes look tangled in English translations — James is drawing the distinction precisely because it matters.",
    },
    {
      q: "Is being tempted a sin?",
      a: "Hebrews 4:15 makes that impossible: Jesus is described as tempted in all points, yet without sin. The arrival of a desire is not the failure. What is done next is a separate question, and treating the two as one is how a manageable struggle turns into despair.",
    },
    {
      q: "What is the way of escape in 1 Corinthians 10:13?",
      a: "The text does not specify, which is the honest answer. In practice it is usually ordinary and physical — leave the room, call someone, put the device in a drawer, go to bed. The verse promises the door exists; it does not promise it will be opened for you.",
    },
  ],
  related: [
    "christian-app-blocker-guide",
    "phone-addiction-bible-verses",
    "how-to-stop-scrolling-at-night",
  ],
  relatedTopics: [
    "bible-verses-about-self-control",
    "bible-verses-about-comparison",
  ],
};
